import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../../services/api';

const Footer = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const [aboutInfo, setAboutInfo] = useState(null);
  const [quickLinks, setQuickLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const currentYear = new Date().getFullYear();

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch contact information
      const contactResponse = await axios.get(`${API_BASE_URL}/contact_info.php?format_phones=true`);
      
      if (contactResponse.data.success && contactResponse.data.data.length > 0) {
        const activeContact = contactResponse.data.data
          .filter(contact => contact.is_active === "1" || contact.is_active === 1)
          .sort((a, b) => a.display_order - b.display_order)[0];
        
        setContactInfo(activeContact);
      } else {
        setError('No contact information available');
      }

      // Fetch about information from public/about.php
      const aboutResponse = await axios.get(`${API_BASE_URL}/public/about.php`);
      
      if (aboutResponse.data.success && aboutResponse.data.data) {
        setAboutInfo(aboutResponse.data.data);
      }

      // Fetch quick links from quick_links.php
      const quickLinksResponse = await axios.get(`${API_BASE_URL}/quick_links.php`);
      
      if (quickLinksResponse.data.success && quickLinksResponse.data.data) {
        const activeLinks = quickLinksResponse.data.data
          .filter(link => link.is_active === "1" || link.is_active === 1)
          .sort((a, b) => a.display_order - b.display_order);
        
        setQuickLinks(activeLinks);
      }
      
    } catch (err) {
      setError('Error loading data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format full address
  const formatFullAddress = (contact) => {
    if (!contact) return '';
    
    const parts = [];
    if (contact.address_line1) parts.push(contact.address_line1.trim());
    if (contact.address_line2) parts.push(contact.address_line2.trim());
    
    const cityState = [];
    if (contact.city) cityState.push(contact.city.trim());
    if (contact.state) cityState.push(contact.state.trim());
    if (cityState.length > 0) parts.push(cityState.join(', '));
    
    if (contact.postal_code) parts.push(contact.postal_code);
    if (contact.country) parts.push(contact.country.trim());
    
    return parts.join(', ');
  };

  // Helper function to format phone number for display
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return '';
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // Format for Indian numbers (10 digits)
    if (digits.length === 10) {
      return `+91 ${digits.substring(0, 5)} ${digits.substring(5)}`;
    }
    // Return original if not standard format
    return phone;
  };

  // Social platforms configuration - will be populated with data from API
  const getSocialPlatforms = () => {
    if (!contactInfo) return [];

    const platforms = [
      { 
        name: 'Facebook', 
        icon: 'fab fa-facebook-f', 
        url: contactInfo.facebook_url || null,
        color: '#1877F2'
      },
      { 
        name: 'Twitter', 
        icon: 'fab fa-twitter', 
        url: contactInfo.twitter_url || null,
        color: '#1DA1F2'
      },
      { 
        name: 'Instagram', 
        icon: 'fab fa-instagram', 
        url: contactInfo.instagram_url || null,
        color: '#E4405F'
      },
      { 
        name: 'YouTube', 
        icon: 'fab fa-youtube', 
        url: contactInfo.youtube_url || null,
        color: '#FF0000'
      },
      { 
        name: 'LinkedIn', 
        icon: 'fab fa-linkedin-in', 
        url: contactInfo.linkedin_url || null,
        color: '#0A66C2'
      }
    ];

    // Filter out platforms that don't have URLs
    return platforms.filter(platform => platform.url && platform.url.trim() !== '');
  };

  const handleSocialClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Function to format office hours
  const formatOfficeHours = () => {
    if (!contactInfo) return 'Not specified';
    
    const hours = [];
    if (contactInfo.mon_fri_hours && contactInfo.mon_fri_hours.trim() !== '') {
      hours.push(`Mon-Fri: ${contactInfo.mon_fri_hours.trim()}`);
    }
    if (contactInfo.saturday_hours && contactInfo.saturday_hours.trim() !== '') {
      hours.push(`Sat: ${contactInfo.saturday_hours.trim()}`);
    }
    if (contactInfo.sunday_hours && contactInfo.sunday_hours.trim() !== '') {
      hours.push(`Sun: ${contactInfo.sunday_hours.trim()}`);
    }
    
    return hours.length > 0 ? hours.join('<br />') : 'Not specified';
  };

  // Loading state
  if (loading) {
    return (
      <footer className="bg-dark text-white py-5 mt-auto">
        <Container className="text-center">
          <Spinner animation="border" variant="warning" />
          <p className="mt-3 text-white-50">Loading contact information...</p>
        </Container>
      </footer>
    );
  }

  return (
    <footer className="bg-dark text-white py-5 mt-auto">
      <Container>
        {error && (
          <Alert variant="warning" className="mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}
        
        <Row>
          <Col lg={4} className="mb-4">
            <h4 className="text-warning mb-4">
              <i className="fas fa-building me-2"></i>
              {contactInfo ? contactInfo.name : 'VTECH CODERS'}
            </h4>
            <p className="text-white-50">
              {/* Use description from about API if available, otherwise use fallback text */}
              {aboutInfo && aboutInfo.description ? 
                aboutInfo.description : 
                'VTECH CODERS is a technology-driven company specializing in website development, software solutions, and digital services. The company focuses on delivering reliable, user-friendly, and customized solutions for educational institutions, businesses, and organizations.'
              }
            </p>
            
            {contactInfo?.website_url && contactInfo.website_url.trim() !== '' && (
              <div className="mb-3">
                <a 
                  href={contactInfo.website_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-outline-warning btn-sm"
                >
                  <i className="fas fa-globe me-2"></i>
                  Visit Our Website
                </a>
              </div>
            )}
            
            <div className="social-links mt-4">
              <div className="d-flex gap-2">
                {getSocialPlatforms().map((platform) => (
                  <Button
                    key={platform.name}
                    variant="outline-light"
                    size="sm"
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ 
                      width: '40px', 
                      height: '40px',
                      borderColor: platform.color,
                      color: platform.color
                    }}
                    onClick={() => handleSocialClick(platform.url)}
                    aria-label={`Follow us on ${platform.name}`}
                    title={`Follow us on ${platform.name}`}
                  >
                    <i className={platform.icon}></i>
                  </Button>
                ))}
              </div>
            </div>
          </Col>
          
          <Col lg={2} md={4} className="mb-4">
            <h5 className="text-white mb-4">Quick Links</h5>
            <ul className="list-unstyled">
              {/* Default Links */}
              <li className="mb-2">
                <Link to="/" className="text-white-50 text-decoration-none">
                  <i className="fas fa-chevron-right me-2 text-warning"></i>
                  Home
                </Link>
              </li>
              <li className="mb-2">
                <Link to="/about" className="text-white-50 text-decoration-none">
                  <i className="fas fa-chevron-right me-2 text-warning"></i>
                  About Us
                </Link>
              </li>
              
              {/* Dynamically loaded quick links from API */}
              {quickLinks.map((link) => (
                <li key={link.id} className="mb-2">
                  {link.link.startsWith('http') ? (
                    // External link
                    <a 
                      href={link.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-white-50 text-decoration-none"
                    >
                      <i className={`${link.icon || 'fas fa-external-link-alt'} me-2 text-warning`}></i>
                      {link.title}
                    </a>
                  ) : (
                    // Internal link
                    <Link 
                      to={link.link} 
                      className="text-white-50 text-decoration-none"
                    >
                      <i className={`${link.icon || 'fas fa-chevron-right'} me-2 text-warning`}></i>
                      {link.title}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </Col>
          
          <Col lg={3} md={4} className="mb-4">
            <h5 className="text-white mb-4">Important Links</h5>
            <ul className="list-unstyled">
              <li className="mb-2">
                <Link to="/ServiceDetail" className="text-white-50 text-decoration-none">
                  <i className="fas fa-external-link-alt me-2 text-warning"></i>
                  Services
                </Link>
              </li>
              <li className="mb-2">
                <Link to="/ProjectsPage" className="text-white-50 text-decoration-none">
                  <i className="fas fa-external-link-alt me-2 text-warning"></i>
                  Projects
                </Link>
              </li>
              <li className="mb-2">
                <Link to="/gallery" className="text-white-50 text-decoration-none">
                  <i className="fas fa-external-link-alt me-2 text-warning"></i>
                  Gallery
                </Link>
              </li>
              <li className="mb-2">
                <Link to="/OwnerProfile" className="text-white-50 text-decoration-none">
                  <i className="fas fa-external-link-alt me-2 text-warning"></i>
                  Profile
                </Link>
              </li>
              <li className="mb-2">
                <Link to="/contact" className="text-white-50 text-decoration-none">
                  <i className="fas fa-external-link-alt me-2 text-warning"></i>
                  Contact US
                </Link>
              </li>
              
            </ul>
          </Col>
          
          <Col lg={3} md={4} className="mb-4">
            <h5 className="text-white mb-4">Contact Info</h5>
            <ul className="list-unstyled">
              <li className="mb-3 d-flex align-items-start">
                <i className="fas fa-map-marker-alt text-warning mt-1 me-2"></i>
                <span className="text-white-50">
                  {contactInfo ? (
                    <div dangerouslySetInnerHTML={{ 
                      __html: formatFullAddress(contactInfo).replace(/\n/g, '<br />')
                    }} />
                  ) : (
                    'Address not available'
                  )}
                </span>
              </li>
              
              {/* Main Phone - Click to Call */}
              {contactInfo?.phone_main && contactInfo.phone_main.trim() !== '' && (
                <li className="mb-3 d-flex align-items-center">
                  <i className="fas fa-phone text-warning me-2"></i>
                  <a 
                    href={`tel:${contactInfo.phone_main.replace(/\D/g, '')}`} 
                    className="text-white-50 text-decoration-none"
                    title="Click to call"
                  >
                    {formatPhoneForDisplay(contactInfo.phone_main)}
                    <i className="fas fa-phone-alt ms-2 text-success small"></i>
                  </a>
                </li>
              )}
              
              {/* Alternate Phone - Click to Call */}
              {contactInfo?.phone_alternate && contactInfo.phone_alternate.trim() !== '' && (
                <li className="mb-3 d-flex align-items-center">
                  <i className="fas fa-mobile-alt text-warning me-2"></i>
                  <a 
                    href={`tel:${contactInfo.phone_alternate.replace(/\D/g, '')}`} 
                    className="text-white-50 text-decoration-none"
                    title="Click to call alternate number"
                  >
                    {formatPhoneForDisplay(contactInfo.phone_alternate)}
                    <i className="fas fa-phone-alt ms-2 text-success small"></i>
                  </a>
                </li>
              )}
              
              {/* General Email */}
              {contactInfo?.email_general && contactInfo.email_general.trim() !== '' && (
                <li className="mb-3 d-flex align-items-center">
                  <i className="fas fa-envelope text-warning me-2"></i>
                  <a 
                    href={`mailto:${contactInfo.email_general}`} 
                    className="text-white-50 text-decoration-none"
                    title="Send email"
                  >
                    {contactInfo.email_general}
                  </a>
                </li>
              )}
              
              {/* Admissions Email (if different) */}
              {contactInfo?.email_admissions && 
               contactInfo.email_admissions.trim() !== '' && 
               contactInfo.email_admissions !== contactInfo.email_general && (
                <li className="mb-3 d-flex align-items-center">
                  <i className="fas fa-user-graduate text-warning me-2"></i>
                  <a 
                    href={`mailto:${contactInfo.email_admissions}`} 
                    className="text-white-50 text-decoration-none"
                    title="Send email for admissions"
                  >
                    {contactInfo.email_admissions}
                  </a>
                </li>
              )}
              
              {/* Office Hours */}
              <li className="d-flex align-items-start">
                <i className="fas fa-clock text-warning mt-1 me-2"></i>
                <span 
                  className="text-white-50" 
                  dangerouslySetInnerHTML={{ 
                    __html: formatOfficeHours()
                  }}
                />
              </li>
            </ul>
          </Col>
        </Row>
        
        <hr className="bg-white-50 my-4" />
        
        <Row>
          <Col md={6} className="text-center text-md-start">
            <p className="mb-2 text-white-50">
              © {currentYear} {contactInfo ? contactInfo.name : 'VTECH CODERS'}. All Rights Reserved.
            </p>
          </Col>
          
          {/* <Col md={6} className="text-center text-md-end">
            <div className="mt-2">
              <Link to="/privacy" className="text-white-50 text-decoration-none me-3">
                Privacy Policy
              </Link>
              <span className="text-white-50">|</span>
              <Link to="/terms" className="text-white-50 text-decoration-none mx-3">
                Terms of Use
              </Link>
              <span className="text-white-50">|</span>
              <Link to="/sitemap" className="text-white-50 text-decoration-none ms-3">
                Site Map
              </Link>
            </div>
          </Col> */}
        </Row>
        
        <Row>
          <Col className="text-center mt-3">
            <p className="mb-0 text-white-50 small">
              <i className="fas fa-map-marker-alt text-warning me-1"></i>
              Registered Office: {contactInfo ? formatFullAddress(contactInfo) : 'Address not available'}
            </p>
          </Col>
        </Row>
      </Container>
      
      <style jsx="true">{`
        .social-links .btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.3);
          background-color: rgba(255,255,255,0.1);
        }
        
        footer a:not(.btn):hover {
          color: #ffc107 !important;
          text-decoration: underline !important;
        }
        
        .social-links .btn {
          transition: all 0.3s ease;
        }
        
        /* Make phone and email links more clickable */
        a.text-white-50:hover {
          color: #ffc107 !important;
          text-decoration: underline !important;
        }
        
        /* Style for phone icons */
        .fa-phone-alt.text-success {
          opacity: 0.8;
          transition: opacity 0.3s ease;
        }
        
        a:hover .fa-phone-alt.text-success {
          opacity: 1;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .social-links .btn {
            width: 36px !important;
            height: 36px !important;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;